import React, {useEffect, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {Add, Delete, Edit} from "@mui/icons-material";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_ADMIN_VALIDATOR_SETS} from "../../components/graphql/queries/gqlAdminSettings";
import {DELETE_ADMIN_VALIDATOR_SET, SAVE_ADMIN_VALIDATOR_SET} from "../../components/graphql/mutations/gqlAdminMutations";
import {VALIDATOR_TYPE_MAP, VALIDATOR_TYPE_OPTIONS, ValidatorSet} from "../adminTypes";
import {SettingRowEditor, SettingRowData} from "./SettingRowEditor";

export function ValidatorSetsTab() {
	const [sets, setSets] = useState<ValidatorSet[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [editingSet, setEditingSet] = useState<ValidatorSet | null>(null);
	const [saving, setSaving] = useState(false);

	const loadSets = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await graphQLServices.documentsClient.query({
				query: GET_ADMIN_VALIDATOR_SETS,
				fetchPolicy: "no-cache",
			});
			setSets((result.data.adminValidatorSets ?? []).map((s: any) => ({
				...s,
				validators: (s.validators ?? []).map((v: any) => ({
					...v,
					validatorType: typeof v.validatorType === "string"
						? (VALIDATOR_TYPE_MAP[v.validatorType] ?? v.validatorType)
						: v.validatorType,
				})),
			})));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadSets(); 
	}, []);

	const handleEdit = (set: ValidatorSet) => setEditingSet(JSON.parse(JSON.stringify(set)));
	const handleNew = () => setEditingSet({id: 0, code: "", description: "", validators: []});

	const handleDelete = async (id: number) => {
		if (!window.confirm("Delete this validator set?")) return;
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: DELETE_ADMIN_VALIDATOR_SET,
				variables: {input: {id}},
			});
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		}
	};

	const handleSave = async () => {
		if (!editingSet) return;
		setSaving(true);
		setError(null);
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: SAVE_ADMIN_VALIDATOR_SET,
				variables: {
					input: {
						id: editingSet.id || undefined,
						code: editingSet.code,
						description: editingSet.description,
						validators: editingSet.validators.map(v => ({
							id: v.id || undefined,
							order: v.order,
							validatorType: v.validatorType,
							settings: v.settings,
							description: v.description,
						})),
					},
				},
			});
			setEditingSet(null);
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	const updateRow = (idx: number, updated: SettingRowData) => {
		if (!editingSet) return;
		const validators = [...editingSet.validators];
		validators[idx] = {
			...validators[idx],
			order: updated.order,
			validatorType: updated.type,
			settings: updated.settings,
			description: updated.description,
		};
		setEditingSet({...editingSet, validators});
	};

	if (loading) return <CircularProgress />;

	return (
		<Box sx={{mt: 2}}>
			{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
			<Button startIcon={<Add />} variant="contained" onClick={handleNew} sx={{mb: 2}}>
				Add Validator Set
			</Button>
			{sets.map(set => (
				<Card key={set.id} sx={{mb: 2}}>
					<CardContent>
						<Typography variant="h6">{set.code}</Typography>
						<Typography variant="body2" color="text.secondary">{set.description}</Typography>
						<Typography variant="caption">{set.validators.length} validator(s)</Typography>
					</CardContent>
					<CardActions>
						<IconButton onClick={() => handleEdit(set)}><Edit /></IconButton>
						<IconButton color="error" onClick={() => handleDelete(set.id)}><Delete /></IconButton>
					</CardActions>
				</Card>
			))}

			<Dialog open={!!editingSet} onClose={() => setEditingSet(null)} maxWidth="lg" fullWidth>
				<DialogTitle>{editingSet?.id ? "Edit Validator Set" : "New Validator Set"}</DialogTitle>
				<DialogContent>
					{editingSet && (
						<Box sx={{mt: 1}}>
							{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
							<TextField fullWidth label="Code" value={editingSet.code}
								onChange={e => setEditingSet({...editingSet, code: e.target.value})}
								sx={{mb: 2}} />
							<TextField fullWidth label="Description" value={editingSet.description}
								onChange={e => setEditingSet({...editingSet, description: e.target.value})}
								sx={{mb: 2}} />
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Order</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Description</TableCell>
										<TableCell>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{[...editingSet.validators]
										.map((v, originalIdx) => ({v, originalIdx}))
										.sort((a, b) => a.v.order - b.v.order)
										.map(({v, originalIdx}) => (
											<SettingRowEditor
												key={v.id || `new-${originalIdx}`}
												setting={{
													id: v.id,
													order: v.order,
													type: v.validatorType,
													settings: v.settings,
													description: v.description,
												}}
												typeOptions={VALIDATOR_TYPE_OPTIONS}
												onChange={updated => updateRow(originalIdx, updated)}
												onDelete={() => setEditingSet({
													...editingSet,
													validators: editingSet.validators.filter((_, i) => i !== originalIdx),
												})}
											/>
										))}
								</TableBody>
							</Table>
							<Button startIcon={<Add />} sx={{mt: 1}} onClick={() =>
								setEditingSet({
									...editingSet,
									validators: [...editingSet.validators, {
										id: 0, order: editingSet.validators.length + 1,
										validatorType: 0, settings: "{}", description: null,
									}],
								})
							}>
								Add Validator
							</Button>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditingSet(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleSave} disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}